import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { getSdk } from '@/lib/sdk';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: () => ['notifications', 'list'] as const,
  unreadCount: () => ['notifications', 'unreadCount'] as const,
  counts: () => ['notifications', 'counts'] as const,
};

/**
 * Paginated notifications with infinite scroll.
 */
export function useNotifications(options?: { unreadOnly?: boolean }) {
  const sdk = getSdk();

  return useInfiniteQuery({
    queryKey: [...notificationKeys.list(), options?.unreadOnly],
    queryFn: async ({ pageParam }) => {
      const response = await sdk.getNotifications({
        cursor: pageParam ?? undefined,
        unreadOnly: options?.unreadOnly,
      });
      return response;
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
  });
}

/**
 * Unread notification count (polls every 30s).
 */
export function useUnreadCount() {
  const sdk = getSdk();

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => sdk.getUnreadNotificationCount(),
    refetchInterval: 30_000,
  });
}

/**
 * Full notification counts (total, read, unread, seen, unseen).
 */
export function useNotificationCounts() {
  const sdk = getSdk();

  return useQuery({
    queryKey: notificationKeys.counts(),
    queryFn: () => sdk.getNotificationCounts(),
    refetchInterval: 30_000,
  });
}

/**
 * Mark a single notification as read.
 */
export function useMarkNotificationRead() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => sdk.markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read.
 */
export function useMarkAllNotificationsRead() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (lastNotificationId?: string) => sdk.markAllNotificationsRead(lastNotificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete a single notification.
 */
export function useDeleteNotification() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) => sdk.deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete multiple notifications.
 */
export function useDeleteNotifications() {
  const sdk = getSdk();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationIds: string[]) => sdk.deleteNotifications(notificationIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
